/**
 * PresentationProgramPreview React component
 * 
 * @author    Jens Gruschel
 * @copyright © 2020 contexagon GmbH
 */


// React
import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';

// Material UI
import Typography from '@material-ui/core/Typography';

// application components
import IdleModeView from "../presentation/kubus/IdleModeView";
import PreludeView from "../presentation/kubus/PreludeView";
import PresentationAbstractView from "../presentation/kubus/PresentationAbstractView";
import PresentationProgramSelectionView from "../presentation/kubus/PresentationProgramSelectionView";
import PageSelection from "../base/PageSelection";
import ViewWidthScaling from "../base/ViewWidthScaling";

// application
import {selectObjectsForIds} from "../../redux/editing/selectors";
import {selectBaseStylesheet} from "../../redux/configuration/selectors";


/**
 * preview for presentation programs
 *
 * @author Jens Gruschel
 */
class PresentationProgramPreview extends React.Component {

    state = {
        pageIndex: 1
    }

    render() {

        if (!this.props.visible) return null;

        const width = this.props.isTop ? 1080 : 300;
        const height = width * 2 * 16 / 9;

        switch (this.props.fieldSet.id) {

            case "general":

                const objectFields = this.props.object.fields || {};
                const items = objectFields.items || [];
                const presentationFilesOrFolders = this.props.findReferencedObjects(items);
                const pageCount = items.length;
                const pageIndex = Math.min(this.state.pageIndex, pageCount);
    
                return <div>
                    {this.props.baseStylesheet && <style>
                        {this.props.baseStylesheet}
                    </style>}
                    <ViewWidthScaling width={width} height={height}>
                        {pageIndex == 0 ? <PresentationProgramSelectionView
                                presentationProgram={this.props.object}
                                findReferencedObjects={this.props.findReferencedObjects}
                            /> :
                            <PresentationAbstractView
                                presentationFileOrFolder={presentationFilesOrFolders[pageIndex - 1]}
                                index={pageIndex - 1}
                                findReferencedObjects={this.props.findReferencedObjects}
                            />
                        }
                    </ViewWidthScaling>
                    {pageCount > 0 && <PageSelection
                        pageNumber={pageIndex + 1}
                        pageCount={pageCount + 1}
                        onChange={(number) => this.setState({pageIndex: number - 1})}
                        onLabel={(number, count) => <Typography variant="button">
                            {number === 1 ? "Auswahl": "Punkt " + (number - 1) + "/" + (count - 1)}
                        </Typography>}
                    />}
                </div>;

            case "idle":

                return <div>
                    {this.props.baseStylesheet && <style>
                        {this.props.baseStylesheet}
                    </style>}
                    <ViewWidthScaling width={width} height={height}>
                        <IdleModeView
                            presentationProgram={this.props.object}
                            findReferencedObjects={this.props.findReferencedObjects}
                        />
                    </ViewWidthScaling>
                </div>;

            case "prelude":
            
                return <div>
                    {this.props.baseStylesheet && <style>
                        {this.props.baseStylesheet}
                    </style>}
                    <ViewWidthScaling width={width} height={height}>
                        <PreludeView
                            presentationProgram={this.props.object}
                            findReferencedObjects={this.props.findReferencedObjects}
                        />
                    </ViewWidthScaling>
                </div>;
    
            default:

                return null;
        }
    }
}

PresentationProgramPreview.propTypes = {
    place: PropTypes.string.isRequired,
    fieldSet: PropTypes.object.isRequired,
    objectType: PropTypes.string,
    objectId: PropTypes.string,
    object: PropTypes.object,
    readOnly: PropTypes.bool,
    onObjectValueChange: PropTypes.func.isRequired
};

const mapStateToProps = (state, props) => {

    const isTop = props.place === "top";
    const visible = ["general", "idle", "prelude"].includes(props.fieldSet.id);

    return {
        isTop: isTop,
        visible: visible,
        baseStylesheet: selectBaseStylesheet(state.configuration),
        findReferencedObjects: field => selectObjectsForIds(state.editing, field)
    }
}

export default connect(mapStateToProps)(PresentationProgramPreview);